import { useState, type ReactNode } from "react";
import { Icon } from "@/components/common/Icon";
import { Spinner } from "@/components/common/Spinner";
import { CARD_ACTION_ICON_SIZE, CARD_ACTION_SIZE, cardActionColor } from "./card-action-style";

const SPRING = "cubic-bezier(0.34, 1.56, 0.64, 1)";

/**
 * 起動 (▶) と停止 (■) を 1 つにまとめたボタン。2 つのアイコンを重ねて置き、操作が切り替わると
 * 片方を縮めて消し、もう片方を弾ませて出す。押下中はボタンごと少し縮める。
 * `busy` の間 (自分で押した起動・停止の完了待ち) はアイコンをスピナーに置き換え、押したことが伝わるようにする。
 * 無効なボタンはホバーを受けないため、ツールチップは外側の要素に付ける。
 */
export function EnvironmentPowerButton({
	action,
	tooltip,
	disabled,
	busy,
	testId,
	onClick,
}: {
	action: "start" | "stop";
	tooltip: string;
	disabled: boolean;
	busy: boolean;
	testId: string;
	onClick: () => void;
}) {
	const [hovered, setHovered] = useState(false);
	const [pressed, setPressed] = useState(false);
	const active = !disabled && pressed;
	// 処理待ちで押せないだけなので、スピナーは暗く沈めずに見せる。
	const dimmed = disabled && !busy;

	return (
		<span data-tooltip={tooltip} style={{ display: "inline-flex" }}>
			<button
				type="button"
				disabled={disabled}
				data-testid={testId}
				onClick={onClick}
				onPointerEnter={() => setHovered(true)}
				onPointerLeave={() => {
					setHovered(false);
					setPressed(false);
				}}
				onPointerDown={() => setPressed(true)}
				onPointerUp={() => setPressed(false)}
				onPointerCancel={() => setPressed(false)}
				style={{
					display: "grid",
					placeItems: "center",
					width: CARD_ACTION_SIZE,
					height: CARD_ACTION_SIZE,
					padding: 0,
					border: 0,
					borderRadius: "var(--radius-2)",
					background: active
						? "var(--overlay-active)"
						: !disabled && hovered
							? "var(--overlay-hover)"
							: "transparent",
					color: cardActionColor(dimmed),
					cursor: disabled ? "not-allowed" : "pointer",
					opacity: dimmed ? 0.5 : 1,
					transform: active ? "scale(0.96)" : "scale(1)",
					transition: `background 120ms ease, transform 200ms ${SPRING}`,
				}}
			>
				<MorphLayer shown={!busy && action === "start"}>
					<Icon name="play" size={CARD_ACTION_ICON_SIZE} />
				</MorphLayer>
				<MorphLayer shown={!busy && action === "stop"}>
					<Icon name="stop" size={CARD_ACTION_ICON_SIZE} />
				</MorphLayer>
				<MorphLayer shown={busy}>
					{busy && (
						<Spinner
							size={CARD_ACTION_ICON_SIZE - 2}
							color={cardActionColor(false)}
							data-testid="environment-power-spinner"
						/>
					)}
				</MorphLayer>
			</button>
		</span>
	);
}

function MorphLayer({ shown, children }: { shown: boolean; children: ReactNode }) {
	return (
		<span
			data-shown={shown}
			style={{
				gridArea: "1 / 1",
				display: "grid",
				transform: shown ? "scale(1)" : "scale(0.5)",
				opacity: shown ? 1 : 0,
				transition: `transform 280ms ${SPRING}, opacity 160ms ease`,
			}}
		>
			{children}
		</span>
	);
}
